import type { PuzzleDef, PuzzleInstanceState, EscapeStateV2 } from './types';
import type {
  HotspotScavengerState,
  PatternMatchState,
  MathLockState,
  ClassifyChecklistState,
  D3ScoreboardState,
  IconSequenceState,
  FrameQuestionState,
  DragAssemblyState,
  PhraseAssemblyState,
  FinalInputState,
} from './types';
import { HotspotScavenger } from './puzzles/HotspotScavenger';
import { PatternMatch } from './puzzles/PatternMatch';
import { MathLock } from './puzzles/MathLock';
import { ClassifyChecklist } from './puzzles/ClassifyChecklist';
import { D3Scoreboard } from './puzzles/D3Scoreboard';
import { IconSequenceLock } from './puzzles/IconSequenceLock';
import { FrameQuestion } from './puzzles/FrameQuestion';
import { DragAssembly } from './puzzles/DragAssembly';
import { PhraseAssembly } from './puzzles/PhraseAssembly';
import { FinalPhraseInput } from './puzzles/FinalPhraseInput';
import { HintPanel } from './HintPanel';

interface PuzzleRouterProps {
  def: PuzzleDef;
  state: EscapeStateV2;
  onSolve: (puzzleId: string) => void;
  patchPuzzle: <T extends PuzzleInstanceState>(id: string, updater: (prev: T) => T) => void;
  grantItem: (itemId: string) => void;
  hintsRevealed: number;
  onRevealHint: () => void;
}

export function PuzzleRouter({
  def,
  state,
  onSolve,
  patchPuzzle,
  grantItem,
  hintsRevealed,
  onRevealHint,
}: PuzzleRouterProps) {
  const { id, params, hints } = def;
  const puzzleState = state.puzzleState[id];

  function patch<T extends PuzzleInstanceState>(updater: (prev: T) => T) {
    patchPuzzle<T>(id, updater);
  }

  function solve() { onSolve(id); }

  let content: React.ReactNode = null;

  switch (params.type) {
    case 'hotspot-scavenger':
      content = (
        <HotspotScavenger
          params={params}
          puzzleState={puzzleState as HotspotScavengerState}
        />
      );
      break;

    case 'pattern-match':
      content = (
        <PatternMatch
          params={params}
          puzzleState={puzzleState as PatternMatchState}
          onSolve={solve}
          patchState={patch}
        />
      );
      break;

    case 'math-lock':
      content = (
        <MathLock
          params={params}
          puzzleState={puzzleState as MathLockState}
          onSolve={solve}
          patchState={patch}
        />
      );
      break;

    case 'classify-checklist':
      content = (
        <ClassifyChecklist
          params={params}
          puzzleState={puzzleState as ClassifyChecklistState}
          onSolve={solve}
          patchState={patch}
        />
      );
      break;

    case 'd3-scoreboard':
      content = (
        <D3Scoreboard
          params={params}
          puzzleState={puzzleState as D3ScoreboardState}
          onSolve={solve}
          patchState={patch}
        />
      );
      break;

    case 'icon-sequence':
      content = (
        <IconSequenceLock
          params={params}
          puzzleState={puzzleState as IconSequenceState}
          onSolve={solve}
          patchState={patch}
        />
      );
      break;

    case 'frame-question':
      content = (
        <FrameQuestion
          params={params}
          puzzleState={puzzleState as FrameQuestionState}
          onSolve={solve}
          onGrantItem={grantItem}
          patchState={patch}
        />
      );
      break;

    case 'drag-assembly':
      content = (
        <DragAssembly
          params={params}
          puzzleState={puzzleState as DragAssemblyState}
          inventory={state.inventory}
          onSolve={solve}
          onGrantItem={grantItem}
          patchState={patch}
        />
      );
      break;

    case 'phrase-assembly':
      content = (
        <PhraseAssembly
          params={params}
          puzzleState={puzzleState as PhraseAssemblyState}
          onSolve={solve}
          patchState={patch}
        />
      );
      break;

    case 'final-input':
      content = (
        <FinalPhraseInput
          params={params}
          puzzleState={puzzleState as FinalInputState}
          onSolve={solve}
          patchState={patch}
        />
      );
      break;
  }

  if (!puzzleState?.solved) {
    return (
      <div className="puzzle-content">
        {content}
        <HintPanel
          puzzleId={id}
          hints={hints}
          revealed={hintsRevealed}
          onReveal={onRevealHint}
        />
      </div>
    );
  }

  return <div className="puzzle-content">{content}</div>;
}
